import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios"; // Ensure axios is imported
import AutoCompleteResult from "./AutoCompleteResult";
import { connect } from "react-redux";
import FloatingInputLabel from "./FloatingInputLabel";
import useDebounce from "./useDebounce"; // Adjust the path as needed
import Spinner from "react-bootstrap/Spinner";

const SearchIndivision = (props) => {
   const comRef = useRef(null);
   const propRef = useRef();
   const [comChoice, setComChoice] = useState(null);
   const [comChoiceLabel, setComChoiceLabel] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [propChoice, setPropChoice] = useState(null);
   const [results, setResults] = useState([]);
   const [resultsProp, setResultsProp] = useState([]);
   const [query, setQuery] = useState("");
   const [queryProp, setQueryProp] = useState("");

   // Debounced queries
   const debouncedQuery = useDebounce(query, 300);
   const debouncedQueryProp = useDebounce(queryProp, 300);

   // on mount, load saved commune if any
   useEffect(() => {
      const savedCode = localStorage.getItem('comChoice');
      const savedLabel = localStorage.getItem('comChoiceLabel');
      if (savedCode && savedLabel) {
         setComChoice(savedCode);
         setComChoiceLabel(savedLabel);
      }
   }, []);
   // Initialize with com if exists
   useEffect(() => {
      const initResult = async () => {
         try {
            if (props.levelTerr.trim() === "com") {
               const codesTerr = props?.entityCodeTerr ? JSON.parse(props.entityCodeTerr) : null;
               const comCode = codesTerr ? codesTerr[0].code : "";

               if (comCode) {
                  setComChoice(comCode);
                  setComChoiceLabel(props.userComLabel);
               }
            }
         } catch (error) {
            console.error("Error initializing results:", error);
            setIsLoading(false);
         }
      };
      initResult();
      // Removed jQuery tooltip initialization
   }, [props.levelTerr, props.entityCodeTerr, comChoice]);

   // Fetch commune results when debouncedQuery changes
   useEffect(() => {
      if (debouncedQuery && debouncedQuery.length >= (props.minCharacters || 3)) {
         getResults(debouncedQuery);
      } else {
         setResults([]);
      }
   }, [debouncedQuery]);

   // Fetch proprietor results when debouncedQueryProp changes
   useEffect(() => {
      if (debouncedQueryProp && debouncedQueryProp.length >= (props.minCharacters || 3)) {
         getResultsProp(debouncedQueryProp);
      } else {
         setResultsProp([]);
      }
   }, [debouncedQueryProp]);

   // Handler functions using useCallback to prevent unnecessary re-renders
   const handleChange = useCallback((e) => {
      setQuery(e.target.value);
   }, []);

   const handleResetCom = useCallback(() => {
      localStorage.removeItem('comChoice');
      localStorage.removeItem('comChoiceLabel');
      setPropChoice(null);
      setComChoice(null);
      setComChoiceLabel(null);
      props.displayPropList(false);
   }, [props]);

   const handleChangeProp = useCallback((e) => {
      setQueryProp(e.target.value);
   }, []);

   // Fetch commune results
   const getResults = async (searchQuery) => {
      if (!searchQuery || searchQuery.length < (props.minCharacters || 3)) {
         setResults([]);
         return;
      }
      try {
         setIsLoading(true);
         const result = await axios.post("Api/geodata/commune_ref", { search: searchQuery });
         setResults(result.data.data);
      } catch (error) {
         console.error("Error fetching commune results:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Fetch proprietor results
   const getResultsProp = async (searchQueryProp) => {
      if (!searchQueryProp || searchQueryProp.length < (props.minCharacters || 3)) return;

      const url = `/Api/geodata/search/indivi/${comChoice}/${searchQueryProp}`;

      try {
         const response = await axios.get(url);
         setResultsProp(response.data);
      } catch (error) {
         console.error("Error fetching proprietor results:", error);
      }
   };

   // Select commune result
   const selectResult = async (label, insee) => {
      try {
         setIsLoading(true);
         const result = await axios.get(
            `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(label)}&citycode=${insee}&type=municipality`
         );
         if (result?.data?.features.length) {
            const currentResult = result.data.features[0];
            setComChoice(currentResult.properties.citycode);
            setComChoiceLabel(currentResult.properties.city);
            localStorage.setItem('comChoice', currentResult.properties.citycode);
            localStorage.setItem('comChoiceLabel', currentResult.properties.city);
            props.updateCenter(currentResult.geometry.coordinates);
            setResults([]);
         }
      } catch (error) {
         console.error("Error selecting result:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Select proprietor result
   const selectResultProp = (id) => {
      const currentResult = resultsProp.find((item) => item.ddenom === id);
      if (currentResult) {
         setPropChoice(currentResult);
         getProprioIndivision(currentResult);
      }
   };

   // Fetch proprietor indivision data
   const getProprioIndivision = async (selectedItem) => {
      const params = {
         nom: selectedItem.gtoper === "1" ? selectedItem.dnomlp : selectedItem.ddenom,
         prenom: selectedItem.gtoper === "1" ? selectedItem.dprnlp : "",
         code_com: comChoice,
         proptype: "indivi",
      };
      props.setPropType("indivi");
      const url = "/Api/geodata/vmap/prop/indivision";

      try {
         const result = await axios.post(url, params);
         if (result.data && result.data.length > 0) {
            props.setPropParcelles(result.data);
         }
         props.displayPropList(true);
      } catch (err) {
         console.error("Error fetching parcel data:", err);
      }
   };

   return (
      <div className="d-flex flex-column align-items-md-center justify-content-start w-100 my-2">
         {!comChoice ? (
            <Fragment>
               <div className="form-group w-100 mb-0 position-relative">
                  <FloatingInputLabel
                     label="Commune insee"
                     className="input-proprioC"
                     onChange={handleChange}
                     value={query}
                     name="comSearchInput"
                  />
                  {isLoading && (
                     <Spinner
                        animation="border"
                        size="sm"
                        className="top-50"
                     />
                  )}
               </div>
               <AutoCompleteResult results={results} selectResult={selectResult} />

            </Fragment>
         ) : comChoiceLabel ? (  
            <div className="d-flex mt-3 bg-std-dark align-items-center w-100">
               <input
                  type="text"
                  value={`${comChoiceLabel} (${comChoice})`}
                  aria-label="Propriétaire"
                  className="form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1 floating-input-value"
                  readOnly
                  />
               <button
                  className="btn btn-link border-0 ms-2"
                  type="button"
                  onClick={handleResetCom}
                  aria-label="Clear"
               >
                  <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
               </button>
            </div>
         ) : (
            <div className="d-flex mt-3 bg-std-dark align-items-center w-100">
               <input
                  type="text"
                  value={comChoice}
                  aria-label="Propriétaire"
                  className="form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1 floating-input-value"
                  readOnly
                  />
               <button
                  className="btn btn-link border-0 ms-2"
                  type="button"
                  onClick={handleResetCom}
                  aria-label="Clear"
               >
                  <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
               </button>
            </div>
         )}

         <div className="form-group w-100 my-2 mt-3">
            {!comChoice ? (
               <div className="did-floating-label-content d-flex bg-std-dark align-items-center w-100">
                  <input
                     type="text"
                     aria-label="Propriétaire"
                     className="did-floating-input-m form-control filter-tools-input text-center input-proprioN flex-grow-1"
                     placeholder=""
                     ref={propRef}
                     value={queryProp || ""} // Bind the input's value to queryProp
                     onChange={handleChangeProp}
                  />
                  <label className="did-floating-label-m">Propriétaire</label>
               </div>
            ) : !propChoice ? (
               <div className="did-floating-label-content d-flex bg-std-dark align-items-center w-100">
                  <input
                     type="text"
                     aria-label="Propriétaire"
                     className="did-floating-input-m form-control filter-tools-input text-center input-proprioN flex-grow-1"
                     placeholder=""
                     ref={propRef}
                     value={queryProp}  
                     onChange={handleChangeProp}
                  />
                  <label className="did-floating-label-m">Propriétaire</label>
               </div>
            ) : (
               <div className="did-floating-label-content d-flex bg-std-dark align-items-center w-100">
                  <input
                     type="text"
                     value={propChoice?.ddenom}
                     aria-label="Propriétaire"
                     className="did-floating-input-m form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1 floating-input-value"
                     ref={propRef}
                     onChange={handleChangeProp}
                  />
                  <button
                     className="btn btn-link border-0 ms-2"
                     type="button"
                     onClick={() => setPropChoice(null)}
                     aria-label="Clear"
                  >
                     <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
                  </button>
                  <label className="did-floating-label-m">Propriétaire</label>
               </div>
            )}
            <AutoCompleteResult
               type={props.propType}
               results={resultsProp}
               selectResult={selectResultProp}
               maxResults={9}
            />
         </div>

      </div>
   );
};

const mapStateToProps = (state) => ({
   levelTerr: state.MeReducer?.entity?.level_terr,
   userCom: state.MapReducer.currentCom,
   userComLabel: state.MapReducer.currentComLabel,
   filterToolStat: state.SearchReducer.filterToolStat,
   entityCodeTerr: state.MeReducer.entity.codes_terr,
});

const mapDispatchToProps = (dispatch) => ({
   updateCenter: (coordinates, z = 17) =>
      dispatch({
         type: "UPDATE_CENTER_MAP",
         data: { centerMap: coordinates, zoom: z },
      }),
   updateVectorLayer: (geoJsonData) => dispatch({ type: "UPDATE_VECTOR_LAYER", data: { geoJsonData } }),
   displayPropList: (display) =>
      dispatch({
         type: "DISPLAY_PROP_LIST_PARCELLES",
         data: { displayPropList: display },
      }),
   setPropType: (propType) => dispatch({ type: "SET_PROP_TYPE", data: { propType } }),
   setPropParcelles: (parcelles) =>
      dispatch({
         type: "SET_PROP_PARCELLES",
         data: { propParcelles: parcelles },
      }),
   setGeomPropParcelles: (geomParcelles) =>
      dispatch({
         type: "SET_PROP_GEOM_PARCELLES",
         data: { geomParcelles },
      }),
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchIndivision);
